import React, { useState, useEffect } from "react";
import {
  PDFViewer,
  Page,
  Text,
  View,
  Document,
} from "@react-pdf/renderer";
import axios from "axios";
import config from "../../../config";
import { calculateColumnTotal } from "../../Shared/Utility";
import moment from "moment";
import { Spin } from "antd";
import PrintHeaderAccount from "../../../Pdf/PrintHeaderAccount";
import PrintFooter from "../../../Pdf/PrintFooter";
import { saleReportStyles } from "../../../Pdf/pdfStyles";
import {
  PrintFormateNumber,
  parseReportAmount,
} from "../../../Pdf/PrintFormateNumber";

const FULL = 567;
const COL1 = 340;
const COL2 = 227;

const NOZZLE_COL = { product: 90, opening: 70, closing: 70, used: 110 };
const PAIR_COL = { label: 120, amount: 50 };
const PAIR_TABLE = PAIR_COL.label + PAIR_COL.amount;

const SALE_COL = {
  sr: 22,
  customer: 90,
  product: 60,
  price: 55,
  qty: 55,
  amount: 58,
};

const STOCK_COL = { product: 70, qty: 45, price: 45, total: 67 };
const CLOSE_COL = { sr: 22, code: 45, product: 80, qty: 45, unit: 35 };
const TRANSFER_COL = { from: 75, to: 75, amount: 77 };

const fmt = (val, decimals = 0) =>
  PrintFormateNumber(parseReportAmount(val), decimals);

const fmtQty = (val) => PrintFormateNumber(parseReportAmount(val));

const SectionTitle = ({ children }) => (
  <View style={{ marginTop: 4, marginBottom: 2 }}>
    <Text
      style={{
        fontSize: 8,
        fontWeight: "bold",
        fontFamily: "Roboto",
        color: "#000",
      }}
    >
      {children}
    </Text>
  </View>
);

const ThCell = ({ width, label, align }) => (
  <View style={[saleReportStyles.th, { width, textAlign: align || "left" }]}>
    <Text style={saleReportStyles.thValue}>{label}</Text>
  </View>
);

const TdCell = ({ width, value, align }) => (
  <View style={[saleReportStyles.td, { width, textAlign: align || "left" }]}>
    <Text style={saleReportStyles.tdValue}>{value ?? "—"}</Text>
  </View>
);

function PairTable({ title, rows, getLabel, total }) {
  const list = rows || [];
  const rowTotal =
    total !== undefined ? total : calculateColumnTotal(list, "Amount");

  return (
    <View style={{ marginBottom: 4 }}>
      <SectionTitle>{title}</SectionTitle>
      <View style={[saleReportStyles.table, { width: PAIR_TABLE }]}>
        <View style={saleReportStyles.tableHead}>
          <ThCell width={PAIR_COL.label} label="Account / Description" />
          <ThCell width={PAIR_COL.amount} label="Amount" align="right" />
        </View>
        {list.map((item, index) => (
          <View key={index} style={saleReportStyles.row}>
            <TdCell width={PAIR_COL.label} value={getLabel(item)} />
            <TdCell width={PAIR_COL.amount} value={fmt(item.Amount)} align="right" />
          </View>
        ))}
        <View style={saleReportStyles.totalsRow}>
          <TdCell width={PAIR_COL.label} value="Total" align="right" />
          <TdCell width={PAIR_COL.amount} value={fmt(rowTotal)} align="right" />
        </View>
      </View>
    </View>
  );
}

function PrintDatewiseSummary() {
  const AccessKey = localStorage.getItem("AccessKey");
  const UserID = localStorage.getItem("ID");

  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [closingStock, setClosingStock] = useState([]);
  const [stockSales, setStockSales] = useState([]);
  const [listOfReading, setListOfReading] = useState([]);
  const [listOfSales, setListOfSale] = useState([]);
  const [paymentReceived, setPaymentReceived] = useState([]);
  const [Expenses, setExpenses] = useState([]);
  const [listOfCashGiven, setListOfCashGiven] = useState([]);
  const [listOfSupplierTransfer, setListOfSupplierTransfer] = useState([]);
  const [cashGivenThroughBank, setCashGivenThroughBank] = useState([]);
  const [cashReceivedInBank, setCashReceivedInBank] = useState([]);

  const urlParams = new URLSearchParams(window.location.search);
  const Dated = urlParams.get("Dated");

  useEffect(() => {
    document.title = "Print DateWise Summary";
    if (!Dated || !UserID || !AccessKey) {
      setLoading(false);
      return;
    }

    setLoading(true);
    axios({
      method: "post",
      url: config.base_url + "Report/GetDailySummaryReport1",
      headers: { "Content-Type": "application/json" },
      data: {
        UserID,
        AccessKey,
        Dated: moment(Dated).isValid()
          ? moment(Dated).format("YYYY-MM-DD")
          : Dated,
      },
    })
      .then(function (response) {
        if (response.data.status_code == 1) {
          setData(response.data);
          setClosingStock(response.data.ListofClosingStock || []);
          setStockSales(response.data.listofStockSales || []);
          setListOfReading(response.data.listofReading || []);
          setListOfSale(response.data.ListofProducts || []);
          setPaymentReceived(response.data.listofCashreceipt || []);
          setExpenses(response.data.listofExpenses || []);
          setListOfCashGiven(response.data.listofCashGiven || []);
          setListOfSupplierTransfer(
            response.data.listofCustomerSupplierTransfers || []
          );
          setCashGivenThroughBank(response.data.listofCashGivenBank || []);
          setCashReceivedInBank(response.data.listofBankReceipts || []);
        }
        setLoading(false);
      })
      .catch(function () {
        setLoading(false);
      });
  }, [Dated, UserID, AccessKey]);

  const datedLabel =
    Dated && moment(Dated).isValid()
      ? moment(Dated).format(config.date_format)
      : "—";

  const totalPaid =
    parseReportAmount(data.cashGiven) + parseReportAmount(data.expensesPaid);

  const salesTotal = calculateColumnTotal(listOfSales, "Amount");
  const stockSalesTotal = calculateColumnTotal(stockSales, "Amount");
  const transferTotal = calculateColumnTotal(listOfSupplierTransfer, "Amount");

  const MyDocument = () => (
    <Document>
      <Page size="A4" style={saleReportStyles.page} wrap>
        <PrintHeaderAccount compact />
        <View style={saleReportStyles.section}>
          <View style={saleReportStyles.titleBlock}>
            <Text style={saleReportStyles.pagetitle}>Daily Summary Sheet</Text>
            <Text style={saleReportStyles.reportSubtitle}>
              Date: {datedLabel}
            </Text>
          </View>

          {listOfReading.length > 0 && (
            <>
              <SectionTitle>Nozzle / Pump Readings</SectionTitle>
              <View style={[saleReportStyles.table, { width: FULL }]}>
                <View style={saleReportStyles.tableHead}>
                  <ThCell width={NOZZLE_COL.product} label="Product" />
                  <ThCell width={NOZZLE_COL.opening} label="Opening" align="right" />
                  <ThCell width={NOZZLE_COL.closing} label="Closing" align="right" />
                  <ThCell width={NOZZLE_COL.used} label="Used" align="right" />
                </View>
                {listOfReading.map((item, index) => {
                  const opening = parseReportAmount(item.Current_Reading);
                  const closing = parseReportAmount(item.Last_Reading);
                  const used = opening - closing;
                  return (
                    <View key={index} style={saleReportStyles.row}>
                      <TdCell width={NOZZLE_COL.product} value={item.ProductCode} />
                      <TdCell
                        width={NOZZLE_COL.opening}
                        value={fmtQty(opening)}
                        align="right"
                      />
                      <TdCell
                        width={NOZZLE_COL.closing}
                        value={fmtQty(closing)}
                        align="right"
                      />
                      <TdCell width={NOZZLE_COL.used} value={fmtQty(used)} align="right" />
                    </View>
                  );
                })}
              </View>
            </>
          )}

          <View style={{ flexDirection: "row", width: FULL, marginTop: 4 }}>
            <View style={{ width: COL1, paddingRight: 6 }}>
              <View style={{ flexDirection: "row" }}>
                <View style={{ width: PAIR_TABLE, marginRight: 4 }}>
                  <PairTable
                    title="Payments Received"
                    rows={paymentReceived}
                    getLabel={(item) => item.Credit_Account?.Name || "—"}
                  />
                </View>
                <View style={{ width: PAIR_TABLE }}>
                  <PairTable
                    title="Cash Paid"
                    rows={listOfCashGiven}
                    getLabel={(item) => item.Debit_Account?.Name || "—"}
                  />
                </View>
              </View>

              <View style={{ flexDirection: "row" }}>
                <View style={{ width: PAIR_TABLE, marginRight: 4 }}>
                  <PairTable
                    title="Payments Received in Banks"
                    rows={cashReceivedInBank}
                    getLabel={(item) => item.Debit_Account?.Name || "—"}
                  />
                </View>
                <View style={{ width: PAIR_TABLE }}>
                  <PairTable
                    title="Cash Paid Through Banks"
                    rows={cashGivenThroughBank}
                    getLabel={(item) => item.Debit_Account?.Name || "—"}
                  />
                </View>
              </View>

              <PairTable
                title="Expenses"
                rows={Expenses}
                getLabel={(item) => item.Description || item.Debit_Account?.Name || "—"}
              />

              <SectionTitle>Sales Detail</SectionTitle>
              <View style={[saleReportStyles.table, { width: COL1 - 6 }]}>
                <View style={saleReportStyles.tableHead}>
                  <ThCell width={SALE_COL.sr} label="Sr." />
                  <ThCell width={SALE_COL.customer} label="Customer" />
                  <ThCell width={SALE_COL.product} label="Product" />
                  <ThCell width={SALE_COL.price} label="Price" align="right" />
                  <ThCell width={SALE_COL.qty} label="Qty" align="right" />
                  <ThCell width={SALE_COL.amount} label="Amount" align="right" />
                </View>
                {listOfSales.map((item, index) => (
                  <View key={index} style={saleReportStyles.row}>
                    <TdCell width={SALE_COL.sr} value={String(index + 1)} />
                    <TdCell
                      width={SALE_COL.customer}
                      value={item.Account_BE?.Name}
                    />
                    <TdCell
                      width={SALE_COL.product}
                      value={item.Product_BE?.ProductCode}
                    />
                    <TdCell
                      width={SALE_COL.price}
                      value={fmtQty(item.Price)}
                      align="right"
                    />
                    <TdCell
                      width={SALE_COL.qty}
                      value={fmtQty(item.Quantity)}
                      align="right"
                    />
                    <TdCell
                      width={SALE_COL.amount}
                      value={fmt(item.Amount)}
                      align="right"
                    />
                  </View>
                ))}
                <View style={saleReportStyles.totalsRow}>
                  <View
                    style={[
                      saleReportStyles.td,
                      {
                        width:
                          SALE_COL.sr +
                          SALE_COL.customer +
                          SALE_COL.product +
                          SALE_COL.price +
                          SALE_COL.qty,
                        textAlign: "right",
                      },
                    ]}
                  >
                    <Text style={saleReportStyles.tdValue}>Total</Text>
                  </View>
                  <TdCell
                    width={SALE_COL.amount}
                    value={fmt(salesTotal)}
                    align="right"
                  />
                </View>
              </View>
            </View>

            <View style={{ width: COL2 }}>
              <SectionTitle>Cash Summary</SectionTitle>
              <View style={[saleReportStyles.table, { width: COL2 }]}>
                {[
                  ["Opening Cash", fmt(data.openingCash)],
                  ["+", ""],
                  ["Cash Sales", fmt(data.cashSales)],
                  ["+", ""],
                  ["Payment Received", fmt(data.cashReceivedTotal)],
                  ["=", ""],
                  ["Total Inwards", fmt(data.TotalInwards)],
                  ["Cash Purchases", fmt(data.cashPurchase)],
                  ["+", ""],
                  ["Cash Paid", fmt(data.cashGiven)],
                  ["+", ""],
                  ["Expenses Paid", fmt(data.expensesPaid)],
                  ["=", ""],
                  ["Total Paid", fmt(totalPaid)],
                  ["Closing Cash", fmt(data.closingCash)],
                ].map(([label, value], index) => (
                  <View key={index} style={saleReportStyles.row}>
                    <TdCell width={COL2 - 70} value={label} />
                    <TdCell width={70} value={value} align="right" />
                  </View>
                ))}
              </View>

              <SectionTitle>Stock Sales</SectionTitle>
              <View style={[saleReportStyles.table, { width: COL2 }]}>
                <View style={saleReportStyles.tableHead}>
                  <ThCell width={STOCK_COL.product} label="Product" />
                  <ThCell width={STOCK_COL.qty} label="Qty" align="right" />
                  <ThCell width={STOCK_COL.price} label="Price" align="right" />
                  <ThCell width={STOCK_COL.total} label="Total" align="right" />
                </View>
                {(stockSales || []).map((item, index) => (
                  <View key={index} style={saleReportStyles.row}>
                    <TdCell width={STOCK_COL.product} value={item.Product_BE?.Name} />
                    <TdCell
                      width={STOCK_COL.qty}
                      value={fmtQty(item.Quantity)}
                      align="right"
                    />
                    <TdCell
                      width={STOCK_COL.price}
                      value={fmtQty(item.Price)}
                      align="right"
                    />
                    <TdCell
                      width={STOCK_COL.total}
                      value={fmt(item.Amount)}
                      align="right"
                    />
                  </View>
                ))}
                <View style={saleReportStyles.totalsRow}>
                  <View
                    style={[
                      saleReportStyles.td,
                      {
                        width:
                          STOCK_COL.product + STOCK_COL.qty + STOCK_COL.price,
                        textAlign: "right",
                      },
                    ]}
                  >
                    <Text style={saleReportStyles.tdValue}>Total</Text>
                  </View>
                  <TdCell
                    width={STOCK_COL.total}
                    value={fmt(stockSalesTotal)}
                    align="right"
                  />
                </View>
                <View style={saleReportStyles.row}>
                  <View
                    style={[
                      saleReportStyles.td,
                      {
                        width:
                          STOCK_COL.product + STOCK_COL.qty + STOCK_COL.price,
                        textAlign: "right",
                      },
                    ]}
                  >
                    <Text style={saleReportStyles.tdValue}>Credit Sales</Text>
                  </View>
                  <TdCell
                    width={STOCK_COL.total}
                    value={data.CreditSales != null ? fmt(data.CreditSales) : "—"}
                    align="right"
                  />
                </View>
              </View>

              <SectionTitle>Closing Stock</SectionTitle>
              <View style={[saleReportStyles.table, { width: COL2 }]}>
                <View style={saleReportStyles.tableHead}>
                  <ThCell width={CLOSE_COL.sr} label="Sr." />
                  <ThCell width={CLOSE_COL.code} label="Code" />
                  <ThCell width={CLOSE_COL.product} label="Product" />
                  <ThCell width={CLOSE_COL.qty} label="Qty" align="right" />
                  <ThCell width={CLOSE_COL.unit} label="Unit" />
                </View>
                {closingStock.map((item, index) => (
                  <View key={index} style={saleReportStyles.row}>
                    <TdCell width={CLOSE_COL.sr} value={String(index + 1)} />
                    <TdCell width={CLOSE_COL.code} value={item.ProductCode} />
                    <TdCell width={CLOSE_COL.product} value={item.Name} />
                    <TdCell
                      width={CLOSE_COL.qty}
                      value={fmtQty(item.Balance)}
                      align="right"
                    />
                    <TdCell
                      width={CLOSE_COL.unit}
                      value={item.Measure_Unit_BE?.Name}
                    />
                  </View>
                ))}
              </View>

              {(listOfSupplierTransfer || []).length > 0 && (
                <>
                  <SectionTitle>Transfers</SectionTitle>
                  <View style={[saleReportStyles.table, { width: COL2 }]}>
                    <View style={saleReportStyles.tableHead}>
                      <ThCell width={TRANSFER_COL.from} label="From" />
                      <ThCell width={TRANSFER_COL.to} label="To" />
                      <ThCell width={TRANSFER_COL.amount} label="Amount" align="right" />
                    </View>
                    {listOfSupplierTransfer.map((item, index) => (
                      <View key={index} style={saleReportStyles.row}>
                        <TdCell
                          width={TRANSFER_COL.from}
                          value={item.Credit_Account?.Name}
                        />
                        <TdCell
                          width={TRANSFER_COL.to}
                          value={item.Debit_Account?.Name}
                        />
                        <TdCell
                          width={TRANSFER_COL.amount}
                          value={fmt(item.Amount)}
                          align="right"
                        />
                      </View>
                    ))}
                    <View style={saleReportStyles.totalsRow}>
                      <View
                        style={[
                          saleReportStyles.td,
                          {
                            width: TRANSFER_COL.from + TRANSFER_COL.to,
                            textAlign: "right",
                          },
                        ]}
                      >
                        <Text style={saleReportStyles.tdValue}>Total</Text>
                      </View>
                      <TdCell
                        width={TRANSFER_COL.amount}
                        value={fmt(transferTotal)}
                        align="right"
                      />
                    </View>
                  </View>
                </>
              )}
            </View>
          </View>
        </View>
        <PrintFooter fixed />
      </Page>
    </Document>
  );

  return (
    <div style={{ width: "100%", height: "100vh", overflow: "hidden" }}>
      {loading ? (
        <div
          style={{
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Spin />
        </div>
      ) : (
        <PDFViewer width="100%" height="100%">
          <MyDocument />
        </PDFViewer>
      )}
    </div>
  );
}

export default PrintDatewiseSummary;
